import { ButtonDestructive, ButtonTertiary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { selectWorkspaceConfirmationModalContext } from "features/content-overlay/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ReactElement, useState } from "react"
import * as Icons from "@nova/component-icons"
import { LicenseModel } from "@nova/db-table-licenses"
import { SidePanelGroup, SidePanelLayer } from "@nova/constants"
import { WorkspaceMember } from "@nova/schemas"
import { hideGlobalModal } from "runlevel/actions"
import { DiscriminatedUnion } from "@nova/util-ts"

export type WorkspaceConfirmationModalProps = {
    title?: string
    getTitle?: (entityName?: string, isAddOn?: boolean) => string
    getConfirmText: ({
        title,
        entityName,
    }: {
        title: string | undefined
        entityName: string | undefined
    }) => string
    icon?: { side: "left" | "right"; icon: Icons.IconComponent }
    onConfirm: () => void
    onCancel: () => void
    confirmationContentComponent: ({
        inputText,
        collaborators,
        setInputText,
        license,
        layers,
        groups,
        entityName,
    }: {
        inputText?: string
        collaborators?: DiscriminatedUnion<WorkspaceMember>[]
        setInputText?: (input: string) => void
        license?: LicenseModel
        layers?: SidePanelLayer[]
        groups?: SidePanelGroup[]
        entityName?: string
    }) => ReactElement
    requiresInput?: boolean
    isDestructive?: boolean
}

function removeExtraWhitespace(input?: string): string | undefined {
    return input?.trim().replace(/\s+/g, " ")
}

export const WorkspaceConfirmationModal = ({
    title,
    icon = { side: "left", icon: Icons.Trash },
    getTitle,
    getConfirmText,
    onConfirm,
    onCancel,
    confirmationContentComponent: ConfirmationContentComponent,
    requiresInput,
    isDestructive,
}: WorkspaceConfirmationModalProps) => {
    const dispatch = useAppDispatch()
    const { entityName, collaborators, license, layers, groups } = useAppSelector(
        selectWorkspaceConfirmationModalContext
    )
    const [inputText, setInputText] = useState("")

    const cancelModal = () => {
        onCancel()
        dispatch(hideGlobalModal())
    }
    const modalTitle = title || getTitle?.(entityName, license?.isAddOn) || "Confirmation"
    return (
        <HighPriorityNovaModalContainer
            title={modalTitle}
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={() => {
                            cancelModal()
                        }}
                    />
                    <ButtonDestructive
                        LeftIcon={icon.side === "left" ? icon.icon : undefined}
                        RightIcon={icon.side === "right" ? icon.icon : undefined}
                        onClick={() => {
                            onConfirm()
                            dispatch(hideGlobalModal())
                        }}
                        title={getConfirmText({ title, entityName })}
                        disabled={requiresInput && inputText !== removeExtraWhitespace(entityName)}
                    />
                </div>
            }
            closeModal={cancelModal}
            isDestructive={isDestructive}
        >
            <div className="mt-4">
                <ConfirmationContentComponent
                    inputText={inputText}
                    collaborators={collaborators}
                    setInputText={setInputText}
                    layers={layers}
                    groups={groups}
                    license={license}
                    entityName={entityName}
                />
            </div>
        </HighPriorityNovaModalContainer>
    )
}
